import { throttle, uniqBy } from 'lodash';
import { useEffect, useRef } from 'react';
import { NetworkStatus } from '@apollo/client';
import {
  GetSearchVerifiedUsersQuery,
  SignalEntityTypeEnum,
  SignalTypeEnum,
  OpeningPotentialCandidatesForSourcePageCarouselQuery,
  useOpeningPotentialCandidatesForSourcePageCarouselLazyQuery,
  useGetSearchVerifiedUsersForSourceLazyQuery,
  useGetVerifiedUserForSourceLazyQuery,
  useGetRejectedSearchVerifiedUsersForSourceLazyQuery,
  GetRejectedSearchVerifiedUsersForSourceQuery,
  VerifiedUsersSortOptionEnum,
  useGetBookmarkedVerifiedUsersLazyQuery,
  GetBookmarkedVerifiedUsersQuery,
} from '@src/graphql/generated';
import { handleFetchMore } from '@src/util/handleFetchMore';
import { QueryParams } from '@src/containers/Source/interfaces/queryParams.interface';
import { recordSignals } from '@src/graphql/signals/actions';
import { GroupedTalentDictionaryKey } from '@src/containers/Source/constants/groupedTalentDictionary/enums/groupedTalentDictionaryKey.enum';
import { REJECTED_TALENTS_VARIABLES } from '@src/containers/Source/constants/groupedTalentDictionary';
import { DEFAULT_CURRENCY_CODE } from '@src/constants';
import { getOpeningRecommendedUsersVariables } from '@src/containers/Source/utils/getOpeningRecommendedUsersVariables';
import { useQueryParams } from '@src/customHooks/useQueryParams';
import { getSearchTags } from '../../utils/getSearchTags';
import { UseVerifiedTalentsParams, UseVerifiedTalentsValue } from './interfaces/useVerifiedTalents.interface';

const FIRST_BATCH_LIMIT = 10;
const FETCH_MORE_LIMIT = 5;

export const useVerifiedTalents = ({
  swiperActiveIndex,
  isActiveOpeningLoading,
  openingId,
  excludedUserIds = [],
  recruiterCurrencyCode,
  onLoad,
}: UseVerifiedTalentsParams): UseVerifiedTalentsValue => {
  const { queryParams } = useQueryParams<QueryParams>();
  const currencyCode = recruiterCurrencyCode || DEFAULT_CURRENCY_CODE;

  const stringifiedFilterParams: string = [
    queryParams.groupedTalentsKey,
    queryParams.isLocal,
    queryParams.isOnSite,
    queryParams.isRelocation,
    queryParams.isRemote,
    queryParams.openingId,
    queryParams.joinedFrom,
    queryParams.joinedTo,
    queryParams.place?.id,
    queryParams.salaryRange?.toString(),
    queryParams.searchPatterns?.toString(),
    queryParams.seniorities?.toString(),
    queryParams.specialities?.toString(),
    queryParams.talents?.toString(),
    queryParams.verifiedUsersSortOption,
    isActiveOpeningLoading,
  ].toString();

  const initialStringifiedFilterParams = useRef<string>(stringifiedFilterParams);

  const prevOpeningId = useRef<string | undefined>('');

  const getSortedPaginationOptionsSortBy = (queryParams?: QueryParams): VerifiedUsersSortOptionEnum => {
    if (queryParams?.verifiedUsersSortOption) {
      return queryParams.verifiedUsersSortOption;
    }

    if (queryParams?.groupedTalentsKey === GroupedTalentDictionaryKey.RECENTLY_ACTIVE) {
      return VerifiedUsersSortOptionEnum.LAST_SEEN;
    }

    if (queryParams?.groupedTalentsKey === GroupedTalentDictionaryKey.RECENTLY_JOINED) {
      return VerifiedUsersSortOptionEnum.JOINED_AT;
    }

    return VerifiedUsersSortOptionEnum.RELEVANCE;
  };

  const [fetchVerifiedUser, { data: verifiedUserData, networkStatus: verifiedUserNetworkStatus }] =
    useGetVerifiedUserForSourceLazyQuery({
      fetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: true,
    });

  const [
    fetchVerifiedUsers,
    {
      data,
      networkStatus: verifiedUsersNetworkStatus,
      refetch: refetchVerifiedUsers,
      fetchMore: fetchMoreVerifiedUsers,
      updateQuery: updateVerifiedUsersQuery,
    },
  ] = useGetSearchVerifiedUsersForSourceLazyQuery({
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    onCompleted: data => {
      if (prevOpeningId.current !== openingId) {
        onLoad(data.searchVerifiedUsers.pagination.totalCount);
      }

      prevOpeningId.current = openingId;
    },
  });

  const [
    fetchBookmarkedVerifiedUsers,
    {
      data: bookmarkedVerifiedUsersData,
      networkStatus: bookmarkedVerifiedUsersNetworkStatus,
      refetch: refetchBookmarkedVerifiedUsers,
      fetchMore: fetchMoreBookmarkedVerifiedUsers,
      updateQuery: updateBookmarkedUsersQuery,
    },
  ] = useGetBookmarkedVerifiedUsersLazyQuery({
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    onCompleted: data => {
      if (prevOpeningId.current !== openingId) {
        onLoad(data.bookmarkedVerifiedUsers.pagination.totalCount);
      }

      prevOpeningId.current = openingId;
    },
  });

  const [
    fetchOpeningRecommendedUsers,
    {
      data: openingRecommendedUsers,
      networkStatus: openingRecommendedUsersNetworkStatus,
      refetch: refetchOpeningRecommendedUsers,
      fetchMore: fetchMoreOpeningRecommendedUsers,
      updateQuery: updateOpeningRecommendedUsersQuery,
    },
  ] = useOpeningPotentialCandidatesForSourcePageCarouselLazyQuery({
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    onCompleted: data => {
      if (prevOpeningId.current !== openingId) {
        onLoad(data.opening.recommendedUsers.pagination.totalCount);
      }

      prevOpeningId.current = openingId;
    },
  });

  const [
    fetchRejectedVerifiedUsers,
    {
      data: rejectedVerifiedUsersData,
      networkStatus: rejectedVerifiedUsersNetworkStatus,
      refetch: refetchRejectedVerifiedUsers,
      fetchMore: fetchMoreRejectedVerifiedUsers,
    },
  ] = useGetRejectedSearchVerifiedUsersForSourceLazyQuery({
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    onCompleted: data => {
      if (prevOpeningId.current !== openingId) {
        onLoad(data.rejectedVerifiedUsers.pagination.totalCount);
      }

      prevOpeningId.current = openingId;
    },
  });

  const fetchSingleUser = async (userId: string) => {
    fetchVerifiedUser({
      variables: {
        recruiterCurrencyCode: currencyCode,
        userId: userId,
      },
    });
  };

  useEffect(() => {
    if (queryParams?.userId) {
      fetchSingleUser(queryParams.userId);
    }
  }, []);

  useEffect(() => {
    if (queryParams?.groupedTalentsKey === GroupedTalentDictionaryKey.RECENTLY_REJECTED) {
      if (rejectedVerifiedUsersData) {
        refetchRejectedVerifiedUsers({
          dateRangeInput: REJECTED_TALENTS_VARIABLES.dateRangeInput,
          openingId: queryParams?.openingId,
          paginationOptions: {
            limit: FIRST_BATCH_LIMIT,
          },
          recruiterCurrencyCode: currencyCode,
        });

        return;
      }

      fetchRejectedVerifiedUsers({
        variables: {
          dateRangeInput: REJECTED_TALENTS_VARIABLES.dateRangeInput,
          openingId: queryParams?.openingId,
          paginationOptions: {
            limit: FIRST_BATCH_LIMIT,
          },
          recruiterCurrencyCode: currencyCode,
        },
      });

      return;
    }

    if (queryParams?.groupedTalentsKey === GroupedTalentDictionaryKey.SAVED_TALENTS) {
      if (bookmarkedVerifiedUsersData) {
        refetchBookmarkedVerifiedUsers({
          paginationOptions: {
            limit: FIRST_BATCH_LIMIT,
          },
          recruiterCurrencyCode: currencyCode,
        });

        return;
      }

      fetchBookmarkedVerifiedUsers({
        variables: {
          paginationOptions: {
            limit: FIRST_BATCH_LIMIT,
          },
          recruiterCurrencyCode: currencyCode,
        },
      });

      return;
    }

    if (queryParams?.openingId && !openingRecommendedUsers && !isActiveOpeningLoading) {
      fetchOpeningRecommendedUsers({
        variables: getOpeningRecommendedUsersVariables({
          ...queryParams,
          openingId: queryParams.openingId,
          recruiterCurrencyCode: currencyCode,
        }),
      });
    } else if (queryParams && !queryParams?.openingId && !data && !isActiveOpeningLoading) {
      fetchVerifiedUsers({
        variables: {
          paginationOptions: {
            limit: FIRST_BATCH_LIMIT,
          },
          recruiterCurrencyCode: currencyCode,
          searchParams: getSearchTags(queryParams),
          sortedPaginationOptions: {
            sortBy: getSortedPaginationOptionsSortBy(queryParams),
          },
        },
      });
    }

    if (queryParams?.openingId && openingRecommendedUsers) {
      refetchOpeningRecommendedUsers(
        getOpeningRecommendedUsersVariables({
          ...queryParams,
          openingId: queryParams.openingId,
          recruiterCurrencyCode: currencyCode,
        }),
      );
    } else if (queryParams && !queryParams?.openingId && data) {
      refetchVerifiedUsers({
        paginationOptions: {
          limit: FIRST_BATCH_LIMIT,
        },
        recruiterCurrencyCode: currencyCode,
        searchParams: getSearchTags(queryParams),
        sortedPaginationOptions: {
          sortBy: getSortedPaginationOptionsSortBy(queryParams),
        },
      });
    }
  }, [stringifiedFilterParams]);

  const getPagination = () => {
    if (queryParams?.groupedTalentsKey === GroupedTalentDictionaryKey.RECENTLY_REJECTED) {
      return rejectedVerifiedUsersData?.rejectedVerifiedUsers.pagination;
    }

    if (queryParams?.groupedTalentsKey === GroupedTalentDictionaryKey.SAVED_TALENTS) {
      return bookmarkedVerifiedUsersData?.bookmarkedVerifiedUsers.pagination;
    }

    if (queryParams?.openingId) {
      return openingRecommendedUsers?.opening.recommendedUsers.pagination;
    }

    return data?.searchVerifiedUsers?.pagination;
  };

  const getFeed = () => {
    if (queryParams?.groupedTalentsKey === GroupedTalentDictionaryKey.RECENTLY_REJECTED) {
      return rejectedVerifiedUsersData?.rejectedVerifiedUsers.result;
    }

    if (queryParams?.groupedTalentsKey === GroupedTalentDictionaryKey.SAVED_TALENTS) {
      return bookmarkedVerifiedUsersData?.bookmarkedVerifiedUsers.result;
    }

    if (queryParams?.openingId) {
      return openingRecommendedUsers?.opening.recommendedUsers.result;
    }

    return data?.searchVerifiedUsers.result;
  };

  const pagination = getPagination();
  const feed = getFeed() || [];
  const feedResult =
    verifiedUserData?.verifiedUser &&
    !verifiedUserData?.verifiedUser.recruiterState.hasSeen &&
    initialStringifiedFilterParams.current === stringifiedFilterParams &&
    queryParams?.userId
      ? uniqBy([verifiedUserData?.verifiedUser, ...feed], 'user.id')
      : feed;

  const isFetchMoreLoading = [
    openingRecommendedUsersNetworkStatus,
    verifiedUsersNetworkStatus,
    rejectedVerifiedUsersNetworkStatus,
    bookmarkedVerifiedUsersNetworkStatus,
  ].includes(NetworkStatus.fetchMore);

  const isInitialFetchLoading =
    [
      openingRecommendedUsersNetworkStatus,
      verifiedUsersNetworkStatus,
      verifiedUserNetworkStatus,
      rejectedVerifiedUsersNetworkStatus,
      bookmarkedVerifiedUsersNetworkStatus,
    ].includes(NetworkStatus.loading) ||
    [
      openingRecommendedUsersNetworkStatus,
      verifiedUsersNetworkStatus,
      verifiedUserNetworkStatus,
      rejectedVerifiedUsersNetworkStatus,
      bookmarkedVerifiedUsersNetworkStatus,
    ].includes(NetworkStatus.setVariables) ||
    [
      openingRecommendedUsersNetworkStatus,
      verifiedUsersNetworkStatus,
      verifiedUserNetworkStatus,
      rejectedVerifiedUsersNetworkStatus,
      bookmarkedVerifiedUsersNetworkStatus,
    ].includes(NetworkStatus.refetch);

  const isLoading = isFetchMoreLoading || isInitialFetchLoading;

  const loadMoreRejectedUsers = throttle(() => {
    const hasPagination = pagination?.hasMoreResults && pagination?.maxCursor;

    if (!isLoading && hasPagination) {
      handleFetchMore(fetchMoreRejectedVerifiedUsers, {
        updateQuery: (
          prev: GetRejectedSearchVerifiedUsersForSourceQuery,
          { fetchMoreResult }: { fetchMoreResult: GetRejectedSearchVerifiedUsersForSourceQuery },
        ) => {
          if (!fetchMoreResult) {
            return prev;
          }

          recordSignals({
            currentLimit: prev.rejectedVerifiedUsers.pagination.limit || 0,
            items: feed.map(item => ({
              id: item.userVerification.id,
              type: SignalEntityTypeEnum.USER_VERIFICATION,
            })),
            signalType: SignalTypeEnum.VIEW_USER_VERIFICATION_LIST_ITEM,
          });

          return {
            rejectedVerifiedUsers: {
              ...prev.rejectedVerifiedUsers,
              pagination: {
                ...prev.rejectedVerifiedUsers.pagination,
                hasMoreResults: fetchMoreResult.rejectedVerifiedUsers.pagination.hasMoreResults,
                maxCursor: fetchMoreResult.rejectedVerifiedUsers.pagination.maxCursor,
              },
              result: [...prev.rejectedVerifiedUsers.result, ...fetchMoreResult.rejectedVerifiedUsers.result],
            },
          };
        },
        variables: {
          excludedUserIds: excludedUserIds,
          paginationOptions: {
            limit: FETCH_MORE_LIMIT,
            minCursor: pagination.maxCursor,
          },
        },
      });
    }
  }, 250);

  const loadMoreBookmarkedUsers = throttle(() => {
    const hasPagination = pagination?.hasMoreResults && pagination?.maxCursor;

    if (!isLoading && hasPagination) {
      handleFetchMore(fetchMoreBookmarkedVerifiedUsers, {
        updateQuery: (
          prev: GetBookmarkedVerifiedUsersQuery,
          { fetchMoreResult }: { fetchMoreResult: GetBookmarkedVerifiedUsersQuery },
        ) => {
          if (!fetchMoreResult) {
            return prev;
          }

          recordSignals({
            currentLimit: prev.bookmarkedVerifiedUsers.pagination.limit || 0,
            items: feed.map(item => ({
              id: item.userVerification.id,
              type: SignalEntityTypeEnum.USER_VERIFICATION,
            })),
            signalType: SignalTypeEnum.VIEW_USER_VERIFICATION_LIST_ITEM,
          });

          return {
            bookmarkedVerifiedUsers: {
              ...prev.bookmarkedVerifiedUsers,
              pagination: {
                ...prev.bookmarkedVerifiedUsers.pagination,
                hasMoreResults: fetchMoreResult.bookmarkedVerifiedUsers.pagination.hasMoreResults,
                maxCursor: fetchMoreResult.bookmarkedVerifiedUsers.pagination.maxCursor,
              },
              result: [...prev.bookmarkedVerifiedUsers.result, ...fetchMoreResult.bookmarkedVerifiedUsers.result],
            },
          };
        },
        variables: {
          paginationOptions: {
            limit: FETCH_MORE_LIMIT,
            minCursor: pagination.maxCursor,
          },
        },
      });
    }
  }, 250);

  const loadMoreVerifiedUsers = throttle(() => {
    const hasPagination = pagination?.hasMoreResults && pagination?.maxCursor;

    if (!isLoading && hasPagination) {
      handleFetchMore(fetchMoreVerifiedUsers, {
        updateQuery: (
          prev: GetSearchVerifiedUsersQuery,
          { fetchMoreResult }: { fetchMoreResult: GetSearchVerifiedUsersQuery },
        ) => {
          if (!fetchMoreResult) {
            return prev;
          }

          recordSignals({
            currentLimit: prev.searchVerifiedUsers?.pagination?.limit || 0,
            items: feed.map(item => ({
              id: item.userVerification.id,
              type: SignalEntityTypeEnum.USER_VERIFICATION,
            })),
            signalType: SignalTypeEnum.VIEW_USER_VERIFICATION_LIST_ITEM,
          });

          if (!prev.searchVerifiedUsers) {
            return fetchMoreResult;
          }

          return {
            searchVerifiedUsers: {
              ...prev.searchVerifiedUsers,
              pagination: {
                ...prev.searchVerifiedUsers.pagination,
                hasMoreResults: fetchMoreResult.searchVerifiedUsers.pagination.hasMoreResults,
                maxCursor: fetchMoreResult.searchVerifiedUsers.pagination.maxCursor,
              },
              result: [...prev.searchVerifiedUsers.result, ...fetchMoreResult.searchVerifiedUsers.result],
            },
          };
        },
        variables: {
          excludedUserIds: excludedUserIds,
          paginationOptions: {
            limit: FETCH_MORE_LIMIT,
            minCursor: pagination.maxCursor,
          },
          sortedPaginationOptions: {
            sortBy: getSortedPaginationOptionsSortBy(queryParams),
          },
        },
      });
    }
  }, 250);

  const loadMoreOpeningRecommendedUsers = throttle(() => {
    const hasPagination = pagination?.hasMoreResults && pagination?.maxCursor;

    if (!isLoading && hasPagination && queryParams?.openingId) {
      const openingVerifiedUsersVariables = getOpeningRecommendedUsersVariables({
        ...queryParams,
        excludedUserIds: excludedUserIds,
        openingId: queryParams.openingId,
        recruiterCurrencyCode: currencyCode,
      });
      handleFetchMore(fetchMoreOpeningRecommendedUsers, {
        updateQuery: (
          prev: OpeningPotentialCandidatesForSourcePageCarouselQuery,
          { fetchMoreResult }: { fetchMoreResult: OpeningPotentialCandidatesForSourcePageCarouselQuery },
        ) => {
          if (!fetchMoreResult) {
            return prev;
          }

          recordSignals({
            currentLimit: prev.opening.recommendedUsers.pagination.limit || 0,
            items: feed.map(item => ({ id: item.userVerification.id, type: SignalEntityTypeEnum.USER_VERIFICATION })),
            signalType: SignalTypeEnum.VIEW_USER_VERIFICATION_LIST_ITEM,
          });

          return {
            opening: {
              ...prev.opening,
              recommendedUsers: {
                ...prev.opening.recommendedUsers,
                pagination: {
                  ...prev.opening.recommendedUsers.pagination,
                  hasMoreResults: fetchMoreResult.opening.recommendedUsers.pagination.hasMoreResults,
                  maxCursor: fetchMoreResult.opening.recommendedUsers.pagination.maxCursor,
                },
                result: [...prev.opening.recommendedUsers.result, ...fetchMoreResult.opening.recommendedUsers.result],
              },
            },
          };
        },
        variables: {
          ...openingVerifiedUsersVariables,
          paginationOptions: {
            ...openingVerifiedUsersVariables.paginationOptions,
            limit: FETCH_MORE_LIMIT,
            minCursor: pagination.maxCursor,
          },
        },
      });
    }
  }, 250);

  const removeVerifiedUser = (userId: string) => {
    if (queryParams?.openingId) {
      updateOpeningRecommendedUsersQuery(data => {
        return {
          ...data,
          opening: {
            ...data.opening,
            recommendedUsers: {
              ...data.opening.recommendedUsers,
              result: data.opening.recommendedUsers.result.filter(item => item.user.id !== userId),
            },
          },
        };
      });

      return;
    }

    if (queryParams?.groupedTalentsKey === GroupedTalentDictionaryKey.SAVED_TALENTS) {
      updateBookmarkedUsersQuery(data => {
        return {
          ...data,
          bookmarkedVerifiedUsers: {
            ...data.bookmarkedVerifiedUsers,
            result: data.bookmarkedVerifiedUsers.result.filter(item => item.user.id !== userId),
          },
        };
      });

      return;
    }

    updateVerifiedUsersQuery(data => {
      return {
        ...data,
        searchVerifiedUsers: {
          ...data.searchVerifiedUsers,
          result: data.searchVerifiedUsers.result.filter(item => item.user.id !== userId),
        },
      };
    });
  };

  const loadMore = () => {
    if (isLoading) {
      return;
    }

    if (queryParams?.groupedTalentsKey === GroupedTalentDictionaryKey.RECENTLY_REJECTED) {
      return loadMoreRejectedUsers();
    }

    if (queryParams?.groupedTalentsKey === GroupedTalentDictionaryKey.SAVED_TALENTS) {
      return loadMoreBookmarkedUsers();
    }

    if (queryParams?.openingId) {
      return loadMoreOpeningRecommendedUsers();
    }

    return loadMoreVerifiedUsers();
  };

  useEffect(() => {
    if (feed.length - 3 < swiperActiveIndex) {
      loadMore();
    }

    return () => {
      loadMoreRejectedUsers.cancel();

      loadMoreBookmarkedUsers.cancel();

      loadMoreOpeningRecommendedUsers.cancel();

      loadMoreVerifiedUsers.cancel();
    };
  }, [swiperActiveIndex]);

  return {
    feed: feedResult,
    fetchSingleUser: fetchSingleUser,
    hasMoreResults: !!pagination?.hasMoreResults,
    isFetchMoreLoading: isFetchMoreLoading,
    isInitialFetchLoading: isInitialFetchLoading,
    loadMore: loadMore,
    removeVerifiedUser: removeVerifiedUser,
    sourcingStatistics: openingRecommendedUsers?.opening.sourcingStatistics,
    totalUsersCount: pagination?.totalCount,
  };
};
